import { arrayOf, bool, shape, string, number, func } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import Link from 'components/Link';
import ActionIcon from 'components/ActionIcon';
import CopyLinkAction from 'components/CopyLinkAction';
import FormattedDate from 'components/FormattedDate';
import Picture from 'components/Picture';
import TeaserImage from 'components/TeaserImage';
import IMAGE_SIZES from 'constants/imageSizes';
import { MQ_LARGE } from 'constants/mq';
import { HAS_RECIPE_BOX } from 'constants/site';
import { SPONSORED_SLUG } from 'constants/tags';
import { IS_VIDEO_ATTR } from 'constants/teasers';
import { getLinkProps } from 'util/link';
import { truncate } from 'util/string';
import { hasTermWithSlug } from 'util/terms';
import { getFormattedDate } from 'oxygen-lib-ui/helpers/date';
import TeaserIcons from './icons';
import { updateTeaserAttributes } from './helper';

const { mediumSquare, largePortrait } = IMAGE_SIZES;

export const DEFAULT_SOURCE_SETS = [
  {
    layoutWidth: { value: 100, units: 'vw' },
    imageDims: mediumSquare,
    media: MQ_LARGE
  }
];
export const DEFAULT_FALLBACK_DIMS = largePortrait;

// this format string uses the timezone lib format
const PROMOTED_DATE_FORMAT = '%Y-%m-%d %H:%M %z';

export const TRUNCATED_VARIANTS = ['everydayGrid'];

export const TRUNCATED_HEADLINE_LENGTH = 80;

// prettier-ignore
export const PLACEHOLDER_SRC = 'l_placeholders:tile-light-blue,fl_tiled/https://s3.amazonaws.com/pixtruder/original_images/dd436318cb8c08bf7d17d5838e06e296e0a25e35';

const SaveToRecipeBoxActions = dynamic(
  () => import('components/SaveToRecipeBoxActions'),
  {
    ssr: false
  }
);

export function Teaser(props) {
  const {
    author,
    credit,
    cta,
    dek,
    fallbackDims,
    gtmAttrs: gtmBaseAttrs,
    hasLazyPicture,
    headline: baseHeadline,
    idx,
    imageUrl: baseImageUrl,
    kicker,
    kickerLink,
    placeholders,
    promotedAt,
    postId,
    postUrl,
    recipeId,
    renderer,
    sourceSets,
    tags,
    teaserAttributes: baseTeaserAttributes,
    url,
    variant,
    isRecipeBoxTeaser,
    onClick
  } = props;

  const link = getLinkProps(url);
  const teaserAttributes = updateTeaserAttributes(baseTeaserAttributes, {
    tags,
    url
  });

  const headline = TRUNCATED_VARIANTS.includes(variant)
    ? truncate(baseHeadline, TRUNCATED_HEADLINE_LENGTH)
    : baseHeadline;

  const gtmAttrs = {
    ...gtmBaseAttrs,
    // idx is an int and 0 is falsy, so check for undefined
    ...(idx !== undefined && {
      'data-gtm-teaser-slot': idx + 1
    }),
    ...(promotedAt !== undefined && {
      'data-gtm-teaser-promoted-at': getFormattedDate(
        promotedAt,
        PROMOTED_DATE_FORMAT
      )
    })
  };

  const isSponsored = hasTermWithSlug(tags, SPONSORED_SLUG);

  const teaserClassnames = classnames('Teaser', {
    [`Teaser--${variant}`]: variant,
    // cake === sponsored. In case adblock is looking at classnames
    'Teaser--cake': isSponsored,
    'Teaser--action': cta,
    'Teaser--video': teaserAttributes.includes(IS_VIDEO_ATTR)
  });

  const imageUrl = baseImageUrl || PLACEHOLDER_SRC;

  const teaserImageProps = {
    renderer,
    sourceSets,
    fallbackDims,
    placeholders,
    url: imageUrl,
    hasLazyPicture,
    credit
  };

  const isTextLink =
    variant === 'textLink' ||
    variant === 'simpleList' ||
    variant === 'navTextLink';

  return (
    <div className={teaserClassnames}>
      {!isTextLink && (
        <div className="Teaser__picture">
          {imageUrl && <TeaserImage {...teaserImageProps} />}
          {teaserAttributes.length > 0 && (
            <TeaserIcons teaserAttributes={teaserAttributes} />
          )}
        </div>
      )}

      {isRecipeBoxTeaser && (
        <div className="Teaser__recipebox-actions">
          <button className="Teaser__recipebox-actions__infoIconButton TeaserRecipeCard__editInfoIconButton__share">
            <ActionIcon
              type="email"
              isBranded
              shareProps={{ body: url, subject: headline }}
              linkAttrs={{
                target: '_blank',
                'data-gtm-placement': 'recipe box',
                'data-gtm-url': url
              }}
              variant="recipeBox"
            />
          </button>
          <button className="Teaser__recipebox-actions__infoIconButton TeaserRecipeCard__editInfoIconButton__copy">
            <CopyLinkAction
              placement="recipe box"
              isBranded={false}
              url={url}
            />
          </button>
          <button className="Teaser__recipebox-actions__infoIconButton Teaser__recipebox-actions-visit">
            <ActionIcon
              type="external"
              url={url}
              isBranded
              linkAttrs={{
                target: '_blank',
                'data-gtm-placement': 'recipe box',
                'data-gtm-url': url
              }}
              variant="recipeBox"
            />
          </button>
        </div>
      )}

      <div className="Teaser__body">
        {isSponsored && <div className="Teaser__cake-tag">Partner</div>}
        <Link {...link}>
          {/* eslint-disable-next-line */}
          <a
            className="Teaser__headline Teaser__link"
            role={isRecipeBoxTeaser && onClick ? 'button' : 'link'}
            onClick={event => {
              if (onClick) {
                onClick(event);
              }
            }}
            onKeyPress={event => {
              if (onClick && event.key === 'Enter') {
                onClick(event);
              }
            }}
            dangerouslySetInnerHTML={{
              __html: headline
            }}
            {...gtmAttrs}
          />
        </Link>

        {kicker && kickerLink && (
          <Link {...kickerLink}>
            <a
              className="Teaser__kicker Teaser__kicker-link"
              dangerouslySetInnerHTML={{
                __html: kicker
              }}
              {...gtmAttrs}
            />
          </Link>
        )}
        {kicker && kickerLink === undefined && (
          <div
            className="Teaser__kicker"
            dangerouslySetInnerHTML={{
              __html: kicker
            }}
          />
        )}

        {dek && (
          <div
            className="Teaser__dek"
            dangerouslySetInnerHTML={{
              __html: dek
            }}
          />
        )}

        {author && author.name && (
          <div
            className={`Teaser__byline${
              author.authorUrl ? ' Teaser__byline-link' : ''
            }`}
          >
            {/*
              Hide author image until we're ready to use it. We should lazy load
              this image when we re-enable.
            */}
            {/* author.imageUrl && (
              <div className="Teaser__author-headshot">
                <Image url={author.imageUrl} />
              </div>
            ) */}
            {author.authorUrl && (
              <Link {...getLinkProps(author.authorUrl)}>
                <a
                  className="Teaser__author"
                  dangerouslySetInnerHTML={{
                    __html: author.name
                  }}
                  {...gtmAttrs}
                />
              </Link>
            )}
            {author.authorUrl === undefined && (
              <span dangerouslySetInnerHTML={{ __html: author.name }} />
            )}
          </div>
        )}

        {promotedAt && (
          <div className="Teaser__timestamp">
            <FormattedDate timestamp={promotedAt} />
          </div>
        )}

        {cta && (
          <div className="Teaser__cta">
            <span dangerouslySetInnerHTML={{ __html: cta }} />
          </div>
        )}
      </div>
      {HAS_RECIPE_BOX && recipeId !== undefined && postId !== undefined && (
        <SaveToRecipeBoxActions
          recipeId={recipeId}
          postId={postId}
          postUrl={postUrl}
          gtmAttrs={{ placement: 'Round-Up Recipe Teaser' }}
        />
      )}
    </div>
  );
}

Teaser.propTypes = {
  author: shape({
    imageUrl: string,
    name: string
  }),
  categories: arrayOf(
    shape({
      name: string,
      slug: string
    })
  ),
  credit: string,
  cta: string,
  dek: string,
  fallbackDims: Picture.propTypes.fallbackDims,
  gtmAttrs: shape({}),
  hasLazyPicture: bool,
  headline: string.isRequired,
  idx: number,
  imageUrl: string,
  kicker: string,
  kickerLink: shape({}),
  link: shape({}),
  sourceSets: Picture.propTypes.sourceSets,
  placeholders: arrayOf(
    shape({
      media: string,
      ratio: number
    })
  ),
  postId: string,
  postUrl: string,
  promotedAt: number,
  recipeId: string,
  renderer: string,
  tags: arrayOf(
    shape({
      name: string,
      slug: string
    })
  ),
  teaserAttributes: TeaserIcons.propTypes.teaserAttributes,
  url: string.isRequired,
  variant: string,
  isRecipeBoxTeaser: bool,
  onClick: func
};

Teaser.defaultProps = {
  author: undefined,
  categories: [],
  credit: undefined,
  cta: undefined,
  dek: '',
  fallbackDims: DEFAULT_FALLBACK_DIMS,
  gtmAttrs: undefined,
  hasLazyPicture: true,
  idx: undefined,
  imageUrl: '', // Should we choose a default image here?
  kicker: '',
  kickerLink: undefined,
  link: undefined,
  placeholders: [],
  postId: undefined,
  postUrl: undefined,
  promotedAt: undefined,
  recipeId: undefined,
  renderer: 'html',
  sourceSets: DEFAULT_SOURCE_SETS,
  tags: [],
  teaserAttributes: [],
  variant: '',
  isRecipeBoxTeaser: false,
  onClick: undefined
};

const mapStateToProps = state => {
  const { app } = state;
  return {
    flips: app.flips
  };
};

export default connect(mapStateToProps)(Teaser);
